import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import type { ConnectorParameterSource } from '@/lib/api/types';

export interface ParameterRow {
  name: string;
  description: string;
  source: ConnectorParameterSource;
  required: boolean;
  value: string;
}

export function emptyParameterRow(): ParameterRow {
  return { name: '', description: '', source: 'agent', required: true, value: '' };
}

interface ParameterEditorProps {
  rows: ParameterRow[];
  onChange: (rows: ParameterRow[]) => void;
}

/** Dynamic editor for a connector's declared parameters (agent-supplied or fixed). */
export function ParameterEditor({ rows, onChange }: ParameterEditorProps) {
  function updateRow(index: number, patch: Partial<ParameterRow>) {
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function removeRow(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Parameters</label>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...rows, emptyParameterRow()])}
          data-testid="add-parameter-button"
        >
          Add parameter
        </Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-gray-500">No parameters declared.</p>}
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-2 rounded-md border border-gray-200 p-3 sm:grid-cols-2"
          data-testid="parameter-row"
        >
          <Input
            label="Name"
            value={row.name}
            onChange={(e) => updateRow(index, { name: e.target.value })}
            placeholder="user_id"
            data-testid="parameter-name-input"
          />
          <Select
            label="Source"
            value={row.source}
            onChange={(e) =>
              updateRow(index, { source: e.target.value as ConnectorParameterSource })
            }
            data-testid="parameter-source-select"
          >
            <option value="agent">Agent-supplied</option>
            <option value="fixed">Fixed</option>
          </Select>
          <Input
            label="Description"
            value={row.description}
            onChange={(e) => updateRow(index, { description: e.target.value })}
            placeholder="Optional, shown to the model"
            className="sm:col-span-2"
          />
          {row.source === 'fixed' ? (
            <Input
              label="Value"
              value={row.value}
              onChange={(e) => updateRow(index, { value: e.target.value })}
              placeholder="A1"
              data-testid="parameter-value-input"
            />
          ) : (
            <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={row.required}
                onChange={(e) => updateRow(index, { required: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                data-testid="parameter-required-checkbox"
              />
              Required
            </label>
          )}
          <div className="sm:col-span-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeRow(index)}
              data-testid="remove-parameter-button"
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
